import { useReducer, useEffect, useRef, useState, useCallback } from 'react'
import { createRoot } from 'react-dom/client'
import * as L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

L.Icon.Default.mergeOptions({ iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow })
import 'material-symbols/outlined.css'
import { Dropdown } from './dropdown.js'
import { loadRecents, useRecentLocations } from './locations.js'
import { Chart, DensityScale } from './chart.js'
import { MODELS, PARAMS, MODEL_COLORS, WIND_UNITS, paramsToFetch, hasWindData, stampingFor, API } from './types.js'
import type { WindUnit, PrecipMode, ParamConfig } from './types.js'
import type { RunInfo, ParamData, Trace } from './types.js'

const DEFAULT_LAT = 52.515
const DEFAULT_LON = 13.564

// Resolve a tab's variant picker to a concrete config: the selected param
// (or the default) replaces the centerpiece param, so the rest of the app
// routes through the existing single-param paths with no variant awareness.
function resolveConfig(cfg: ParamConfig, variant: Record<string, string>, precipMode: PrecipMode): ParamConfig {
  // Precip rate/accumulated are two backend params (avg_tprate / tp); the mode
  // selects which, so each resolves to its own fetch + cache entry.
  if (cfg.centerpiece.kind === 'precip') {
    const param = precipMode === 'rate' ? cfg.centerpiece.rateParam : cfg.centerpiece.param
    return { ...cfg, centerpiece: { ...cfg.centerpiece, param } }
  }
  if (!cfg.variants || cfg.centerpiece.kind === 'filled-plume') return cfg
  const chosen = variant[cfg.variants.key] ?? cfg.centerpiece.param
  return { ...cfg, centerpiece: { ...cfg.centerpiece, param: chosen } }
}

function paramsForKey(key: string, variant: Record<string, string>, precipMode: PrecipMode): string[] {
  const config = PARAMS.find(p => p.key === key)
  if (!config) return [key]
  return paramsToFetch(resolveConfig(config, variant, precipMode))
}

function groupForLabel(label: string) {
  return MODELS.find(g => g.label === label)!
}

function initTimesForGroup(runs: RunInfo[], label: string): string[] {
  const models = new Set(groupForLabel(label).apiModels)
  const times = runs.filter(r => models.has(r.model) && r.init_time !== null).map(r => r.init_time!)
  return [...new Set(times)].sort().reverse()
}

function runIdsForSelection(runs: RunInfo[], label: string, initTime: string): string[] {
  const group = groupForLabel(label)
  const models = new Set(group.apiModels)
  // Time-lagged ensembles pool the last `lagWindow` cycles up to and including
  // the selected one; every other model is just the one selected cycle.
  const window = new Set(
    initTimesForGroup(runs, label).filter(t => t <= initTime).slice(0, group.lagWindow ?? 1),
  )
  return runs
    .filter(r => models.has(r.model) && r.init_time !== null && window.has(r.init_time))
    .map(r => r.run_id)
}

function analysisRunId(runs: RunInfo[], label: string): string | null {
  const models = groupForLabel(label).apiModels
  const own = runs.find(r => models.includes(r.model) && r.init_time === null)
  if (own) return own.run_id
  // Fall back to IFS analysis (e.g. AIFS has no own analysis)
  return runs.find(r => r.model === 'ifs' && r.init_time === null)?.run_id ?? null
}

// --- State: pure user intent, no data ---

type Shading = 'off' | 'contour' | 'kde' | 'adaptive' | 'daily'

interface State {
  runs: RunInfo[]
  model: string
  initTime: string
  param: string
  lat: number
  lon: number
  prev: { model: string; initTime: string } | null
  shading: Shading | null   // null = follow the per-view default; concrete = user's explicit pick
  windUnit: WindUnit
  precipMode: PrecipMode
  variant: Record<string, string>   // variant-picker key -> chosen param
  error: string | null
}

type Action =
  | { type: 'RUNS_LOADED'; runs: RunInfo[] }
  | { type: 'SELECT_MODEL'; model: string }
  | { type: 'SELECT_INIT_TIME'; initTime: string }
  | { type: 'SELECT_PARAM'; param: string }
  | { type: 'SELECT_SHADING'; shading: Shading }
  | { type: 'SELECT_WIND_UNIT'; windUnit: WindUnit }
  | { type: 'SELECT_PRECIP_MODE'; precipMode: PrecipMode }
  | { type: 'SELECT_VARIANT'; key: string; value: string }
  | { type: 'SELECT_LOCATION'; lat: number; lon: number }
  | { type: 'TOGGLE_PREV' }
  | { type: 'PIN' }
  | { type: 'UNPIN' }
  | { type: 'ERROR'; error: string }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'RUNS_LOADED': {
      const model = state.model || 'AIFS'
      const times = initTimesForGroup(action.runs, model)
      const initTime = state.initTime && times.includes(state.initTime)
        ? state.initTime : (times[0] ?? '')
      return { ...state, runs: action.runs, model, initTime }
    }
    case 'SELECT_MODEL': {
      // If tracking the current model's latest run, follow "latest" into the
      // new model rather than matching wall-clock time.
      const onLatest = state.initTime === initTimesForGroup(state.runs, state.model)[0]
      const times = initTimesForGroup(state.runs, action.model)
      const initTime = onLatest ? (times[0] ?? '')
        : times.includes(state.initTime) ? state.initTime
        : closestTime(times, state.initTime)
      return { ...state, model: action.model, initTime }
    }
    case 'SELECT_INIT_TIME': {
      // Selecting the pinned run is a swap with it, not a collapse onto it.
      if (state.prev && state.prev.model === state.model && state.prev.initTime === action.initTime) {
        return { ...state, initTime: action.initTime, prev: { model: state.model, initTime: state.initTime } }
      }
      return { ...state, initTime: action.initTime }
    }
    case 'SELECT_PARAM':
      return { ...state, param: action.param, prev: null }
    case 'SELECT_LOCATION':
      return { ...state, lat: action.lat, lon: action.lon, prev: null }
    case 'TOGGLE_PREV': {
      if (!state.prev) return state
      return { ...state, model: state.prev.model, initTime: state.prev.initTime,
               prev: { model: state.model, initTime: state.initTime } }
    }
    case 'PIN':
      return { ...state, prev: { model: state.model, initTime: state.initTime } }
    case 'UNPIN':
      return { ...state, prev: null }
    case 'SELECT_SHADING': return { ...state, shading: action.shading }
    case 'SELECT_WIND_UNIT': return { ...state, windUnit: action.windUnit }
    case 'SELECT_PRECIP_MODE': return { ...state, precipMode: action.precipMode }
    case 'SELECT_VARIANT':
      return { ...state, variant: { ...state.variant, [action.key]: action.value } }
    case 'ERROR': return { ...state, error: action.error }
  }
}

function closestTime(times: string[], target: string): string {
  if (!times.length) return ''
  const t = new Date(target + 'Z').getTime()
  let best = times[0]
  let bestDiff = Math.abs(new Date(best + 'Z').getTime() - t)
  for (const time of times) {
    const diff = Math.abs(new Date(time + 'Z').getTime() - t)
    if (diff < bestDiff) { best = time; bestDiff = diff }
  }
  return best
}

function readURL(): { model?: string; param?: string; initTime?: string; shading?: Shading; windUnit?: string; precipMode?: PrecipMode; variant: Record<string, string>; lat?: number; lon?: number } {
  const p = new URLSearchParams(location.search)
  const s = p.get('shading')
  const lat = p.get('lat'), lon = p.get('lon')
  // Each variant tab reads its declared key back, keeping only recognised values.
  const variant: Record<string, string> = {}
  for (const cfg of PARAMS) {
    if (!cfg.variants) continue
    const v = p.get(cfg.variants.key)
    if (v && cfg.variants.options.some(o => o.value === v)) variant[cfg.variants.key] = v
  }
  // Validate the param against the real tab set at the boundary, so a stale
  // URL (e.g. the retired 'heatx' tab) can't feed an unknown key downstream.
  // Everything past here can then trust param is a real tab.
  const rawParam = p.get('param')
  const param = rawParam && PARAMS.some(c => c.key === rawParam) ? rawParam : undefined
  return {
    model: p.get('model') ?? undefined,
    param,
    initTime: p.get('init_time') ?? undefined,
    shading: (s === 'off' || s === 'contour' || s === 'kde' || s === 'adaptive' || s === 'daily') ? s : undefined,
    windUnit: p.get('wind_unit') ?? undefined,
    precipMode: (p.get('precip_mode') ?? undefined) as PrecipMode | undefined,
    variant,
    lat: lat ? parseFloat(lat) : undefined,
    lon: lon ? parseFloat(lon) : undefined,
  }
}

function writeURL(model: string, param: string, initTime: string, shading: Shading | null, windUnit: WindUnit, precipMode: PrecipMode, variant: Record<string, string>, lat: number, lon: number) {
  const p = new URLSearchParams()
  if (model) p.set('model', model)
  if (param) p.set('param', param)
  if (initTime) p.set('init_time', initTime)
  // Only persist an explicit pick (incl. 'off'); null follows the per-view default.
  if (shading !== null) p.set('shading', shading)
  if (windUnit !== 'km/h') p.set('wind_unit', windUnit)
  if (precipMode !== 'rate') p.set('precip_mode', precipMode)
  // Emit each variant selection under its declared key, only when non-default.
  for (const cfg of PARAMS) {
    if (!cfg.variants) continue
    const v = variant[cfg.variants.key]
    if (v && v !== cfg.variants.options[0].value) p.set(cfg.variants.key, v)
  }
  if (lat !== DEFAULT_LAT || lon !== DEFAULT_LON) {
    p.set('lat', lat.toFixed(4))
    p.set('lon', lon.toFixed(4))
  }
  history.replaceState(null, '', '?' + p.toString())
}

function formatTime(iso: string): string {
  return `${iso.slice(0, 10)} ${iso.slice(11, 13)}z`
}

// --- Cache: owns streams + data ---

// A run's shape as declared by the stream's `init` event. `times` is the run's
// whole grid; `gridIndex` maps a valid_time back to its column, so step events
// are written at their grid position rather than appended in arrival order.
//
// Known limitation: `init` publishes one grid per run, but a run's member types
// can be on different cadences — ICON's ensemble is 6-hourly where its
// deterministic run is hourly — and the declared grid is their union. A coarser
// member type therefore lands on a fraction of the columns, and nothing here
// distinguishes "this member doesn't sample that often" from "this step hasn't
// arrived", so it renders as a band around each of its own readings rather than
// as a continuous plume. Drawing it continuously would need per-member-type
// valid times on the wire: the stream computes them per (member_type, param,
// valid time) and flattens them into a single union before sending.
interface RunMeta {
  model: string
  init_time: string | null
  times: number[]
  gridIndex: Map<number, number>
  params: Set<string>
}

// One member's values, dense over its run's grid. `times` is shared by
// reference with the run's grid, so the same index means the same instant
// across every param of that run — which is what the renderers that pair two
// params by index rely on.
interface MemberValues {
  times: number[]
  values: (number | null)[]
}

// param -> `${run_id}:${member_type}:${member}` -> that member's values.
type Accum = Map<string, Map<string, MemberValues>>

interface CacheEntry {
  data: Record<string, ParamData>
  status: 'loading' | 'complete'
  es: EventSource | null
  idleTimer: ReturnType<typeof setTimeout> | undefined
}

function LocationPicker({ lat, lon, onSelect }: {
  lat: number; lon: number; onSelect: (lat: number, lon: number) => void
}) {
  const [open, setOpen] = useState(false)
  const [placeName, setPlaceName] = useState<string | null>(null)
  const mapRef = useRef<HTMLDivElement>(null)
  const leafletRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)
  const { recents, push, updateName } = useRecentLocations()

  // Persist + apply a chosen location; its name is backfilled once geocoding resolves.
  const selectLocation = useCallback((la: number, lo: number) => {
    push(la, lo)
    onSelect(la, lo)
  }, [push, onSelect])

  // Reverse geocode via Nominatim (zoom=10 → city/town level)
  useEffect(() => {
    const controller = new AbortController()
    setPlaceName(null)
    fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=13`,
      { signal: controller.signal }
    )
      .then(r => r.json())
      .then(d => {
        const a = d.address ?? {}
        // town > city: UK districts misuse `city` (Mole Valley for Dorking);
        // Berlin has no town, falls through to city correctly
        const name = a.town || a.city || a.village || a.hamlet || a.suburb || a.municipality || d.name
        if (name) {
          setPlaceName(name)
          updateName(lat, lon, name)
        }
      })
      .catch(() => {})
    return () => controller.abort()
  }, [lat, lon, updateName])

  useEffect(() => {
    if (!open || !mapRef.current || leafletRef.current) return
    const map = L.map(mapRef.current).setView([lat, lon], 6)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OSM', detectRetina: true,
    } as L.TileLayerOptions).addTo(map)
    const marker = L.marker([lat, lon]).addTo(map)
    map.on('click', (e: L.LeafletMouseEvent) => {
      marker.setLatLng([e.latlng.lat, e.latlng.lng])
      selectLocation(parseFloat(e.latlng.lat.toFixed(4)), parseFloat(e.latlng.lng.toFixed(4)))
    })
    leafletRef.current = map
    markerRef.current = marker
    setTimeout(() => map.invalidateSize(), 0)
    return () => { map.remove(); leafletRef.current = null; markerRef.current = null }
  }, [open])

  useEffect(() => {
    markerRef.current?.setLatLng([lat, lon])
  }, [lat, lon])

  return (
    <div style={{ position: 'relative' }}>
      <button className="label-btn" onClick={() => setOpen(!open)}>
        {placeName ?? `${lat.toFixed(2)}°N ${lon.toFixed(2)}°E`} ▾
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
          marginTop: 4, width: 300, borderRadius: 6, overflow: 'hidden',
          border: '1px solid #ccc', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 10,
          background: '#fff',
        }}>
          <div ref={mapRef} style={{ width: '100%', height: 200 }} />
          <div style={{ borderTop: '1px solid #eee' }}>
            {recents.length === 0 ? (
              <div style={{ padding: '8px 10px', fontSize: 12, color: '#888' }}>
                Click the map to select a location.
              </div>
            ) : recents.map(r => {
              const current = r.lat.toFixed(4) === lat.toFixed(4) && r.lon.toFixed(4) === lon.toFixed(4)
              return (
                <button
                  key={`${r.lat},${r.lon}`}
                  onClick={() => { selectLocation(r.lat, r.lon); setOpen(false) }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px',
                    border: 'none', borderBottom: '1px solid #f2f2f2', background: current ? '#f0f6ff' : '#fff',
                    fontSize: 12, cursor: 'pointer', color: '#333',
                  }}
                >
                  {r.name ?? `${r.lat.toFixed(2)}°N ${r.lon.toFixed(2)}°E`}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// Extended-range ensembles default their temperature plumes to daily min/max
// shading: over a 15-day run the instantaneous plume is too sparse to read.
const DAILY_SHADE_MODELS = new Set(['AIFS', 'IFS', 'GEM'])
const TEMP_PLUME_TABS = new Set(['2t', 'feels-like'])

// The default shading for a view when the user hasn't made an explicit choice.
function defaultShading(model: string, param: string): Shading {
  return TEMP_PLUME_TABS.has(param) && DAILY_SHADE_MODELS.has(model) ? 'daily' : 'off'
}

function App() {
  const url = readURL()
  const lastLocation = loadRecents()[0]
  const [state, dispatch] = useReducer(reducer, {
    runs: [], model: url.model ?? '', initTime: url.initTime ?? '', param: url.param ?? 'meteogram',
    shading: url.shading ?? null,
    windUnit: (url.windUnit as WindUnit) ?? 'km/h',
    precipMode: (url.precipMode as PrecipMode) ?? 'rate',
    variant: url.variant,
    lat: url.lat ?? lastLocation?.lat ?? DEFAULT_LAT,
    lon: url.lon ?? lastLocation?.lon ?? DEFAULT_LON,
    prev: null, error: null,
  })

  const cache = useRef(new Map<string, CacheEntry>())
  const [, bump] = useState(0)

  // Include the resolved fetch params so a variant switch (which changes
  // *which* param is streamed) keys to a distinct cache entry.
  const cacheKey = (model: string, initTime: string) =>
    `${model}:${initTime}:${state.param}:${paramsForKey(state.param, state.variant, state.precipMode).join(',')}:${state.lat}:${state.lon}`

  const refreshRuns = useCallback(() => {
    fetch(`${API}/runs`)
      .then(r => r.json())
      .then((runs: RunInfo[]) => dispatch({ type: 'RUNS_LOADED', runs }))
      .catch(e => dispatch({ type: 'ERROR', error: String(e) }))
  }, [])

  useEffect(() => { refreshRuns() }, [refreshRuns])

  // Close all streams on unmount
  useEffect(() => () => {
    for (const entry of cache.current.values()) {
      clearTimeout(entry.idleTimer)
      entry.es?.close()
    }
  }, [])

  // Reconcile cache with user intent
  useEffect(() => {
    if (!state.model || !state.initTime || !state.param) return

    const currentKey = cacheKey(state.model, state.initTime)
    const prevKey = state.prev ? cacheKey(state.prev.model, state.prev.initTime) : null

    // Evict orphaned entries
    for (const [key, entry] of cache.current) {
      if (key !== currentKey && key !== prevKey) {
        clearTimeout(entry.idleTimer)
        entry.es?.close()
        cache.current.delete(key)
      }
    }

    // Start stream for current if no cache entry
    if (!cache.current.has(currentKey)) {
      startStream(currentKey, state.model, state.initTime)
    }
  }, [state.runs, state.model, state.initTime, state.param, state.variant, state.precipMode, state.lat, state.lon])

  function startStream(key: string, model: string, initTime: string) {
    const rids = runIdsForSelection(state.runs, model, initTime)
    if (!rids.length) return
    const params = paramsForKey(state.param, state.variant, state.precipMode)

    const analysisRid = analysisRunId(state.runs, model)

    const streamUrl = new URL(`${API}/stream`, location.origin)
    for (const rid of rids) streamUrl.searchParams.append('run', rid)
    // Clip every run to valid times >= the selected cycle. For a single-cycle
    // selection this is a no-op (the run starts there anyway); for a
    // time-lagged window it drops the older cycles' pre-T portion so the plume
    // pools all cycles from the selected time forward.
    streamUrl.searchParams.set('since', initTime)
    if (analysisRid) {
      streamUrl.searchParams.append('run', analysisRid)
    }
    for (const p of params) streamUrl.searchParams.append('param', p)
    streamUrl.searchParams.set('lat', String(state.lat))
    streamUrl.searchParams.set('lon', String(state.lon))

    const accum: Accum = new Map()
    const units = new Map<string, string>()
    const runMeta = new Map<string, RunMeta>()
    let lat = state.lat, lon = state.lon
    let lastFlush = 0

    const es = new EventSource(streamUrl.toString())
    const entry: CacheEntry = { data: {}, status: 'loading', es, idleTimer: undefined }
    cache.current.set(key, entry)

    function flush(done: boolean) {
      const data: Record<string, ParamData> = {}
      for (const [param, traces] of accum) {
        const traceList: Trace[] = [...traces.entries()].filter(([k]) => {
          // Time-lagged pooling: keep the ensemble (pf/ef) from every cycle.
          // The deterministic line (fc) is kept only from the selected cycle —
          // one det line, not N stacked lagged ones. The control (cf) is kept
          // from any cycle in the window: a sparse-control ensemble (MOGREPS-UK
          // runs the control every 6 h, and the lag window is 6 cycles) carries
          // exactly one control per window, up to 6 h old — the time-lagged
          // control we want to draw. Gating cf to the selected cycle hid it on
          // the ~5/6 cycles that aren't control cycles. For non-lagged models
          // the window is the selected cycle alone, so cf is unchanged.
          const [run_id, member_type] = k.split(':')
          if (member_type === 'fc') {
            return runMeta.get(run_id)?.init_time === initTime
          }
          return true
        }).map(([k, tv]) => {
          const [run_id, member_type, member] = k.split(':')
          const meta = runMeta.get(run_id)
          // Values are written at their grid column, so arrival order is
          // already irrelevant — no re-sort, and the arrays pass straight
          // through rather than being copied on every flush.
          return {
            run_id, model: meta?.model ?? '', init_time: meta?.init_time ?? null,
            member_type, member: parseInt(member),
            times: tv.times, values: tv.values,
          }
        })
        for (const [run_id, meta] of runMeta) {
          if (!meta.params.has(param)) continue
          // A run can have no valid times (e.g. no analysis data for this
          // time range yet). It has nothing to span, so it gets no anchor:
          // reaching for meta.times[0] on an empty array would pin the x-axis
          // to the epoch (1970), collapsing the data to a sliver.
          if (!meta.times.length) continue
          traceList.push({
            run_id, model: meta.model, init_time: meta.init_time,
            member_type: '_anchor', member: 0,
            times: [meta.times[0], meta.times[meta.times.length - 1]],
            values: [0, 0],
          })
        }
        data[param] = {
          param, unit: units.get(param) ?? '', stamping: stampingFor(param),
          lat, lon, traces: traceList,
        }
      }
      entry.data = data
      if (done) { entry.status = 'complete'; entry.es = null }
      bump(v => v + 1)
    }

    es.addEventListener('init', (e) => {
      const payload = JSON.parse(e.data)
      lat = payload.lat; lon = payload.lon
      for (const [p, info] of Object.entries(payload.params as Record<string, { unit: string }>)) {
        units.set(p, info.unit)
        if (!accum.has(p)) accum.set(p, new Map())
      }
      for (const [run_id, info] of Object.entries(payload.runs as Record<string, { model: string; init_time: string | null; valid_times: string[]; params: string[] }>)) {
        const times = info.valid_times.map(vt => new Date(vt + 'Z').getTime())
        runMeta.set(run_id, {
          model: info.model, init_time: info.init_time,
          times,
          gridIndex: new Map(times.map((t, i) => [t, i])),
          params: new Set(info.params),
        })
      }
      flush(false)
    })

    es.addEventListener('step', (e) => {
      const { run_id, valid_time, member_type, param, values: vals } = JSON.parse(e.data) as {
        run_id: string; valid_time: string; member_type: string; param: string; values: (number | null)[]
      }
      const meta = runMeta.get(run_id)
      if (!meta) {
        console.warn(`Stream step for unknown run_id: ${run_id}`)
        return
      }
      // The stream only reads steps it declared in `init`, so an unrecognised
      // valid_time means that contract broke. Dropping it quietly would shift
      // every later column, which is the failure this grid exists to prevent.
      const col = meta.gridIndex.get(new Date(valid_time + 'Z').getTime())
      if (col === undefined) {
        console.warn(`Stream step off ${run_id}'s declared grid: ${valid_time}`)
        return
      }
      if (!accum.has(param)) accum.set(param, new Map())
      const traces = accum.get(param)!
      // Nulls are written, not skipped: a null carries a real grid position,
      // and for a rate param the slot before a value is the left edge of the
      // interval that value describes.
      for (let i = 0; i < vals.length; i++) {
        const traceKey = `${run_id}:${member_type}:${i}`
        if (!traces.has(traceKey)) {
          traces.set(traceKey, { times: meta.times, values: new Array<number | null>(meta.times.length).fill(null) })
        }
        traces.get(traceKey)!.values[col] = vals[i]
      }

      const now = performance.now()
      if (now - lastFlush > 200) { lastFlush = now; flush(false) }

      clearTimeout(entry.idleTimer)
      entry.idleTimer = setTimeout(() => flush(false), 200)
    })

    es.addEventListener('done', () => {
      clearTimeout(entry.idleTimer)
      flush(true)
      es.close()
    })

    es.addEventListener('error', () => {
      clearTimeout(entry.idleTimer)
      if (accum.size > 0) flush(false)
      es.close()
      entry.es = null
    })
  }

  // Sync state to URL
  useEffect(() => {
    if (state.model && state.param) writeURL(state.model, state.param, state.initTime, state.shading, state.windUnit, state.precipMode, state.variant, state.lat, state.lon)
  }, [state.model, state.param, state.initTime, state.shading, state.windUnit, state.precipMode, state.variant, state.lat, state.lon])

  // Keyboard: left/right step through the current model's runs (older/newer),
  // space swaps with the pinned run.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== ' ') return
      const el = document.activeElement as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      if (document.querySelector('[aria-expanded="true"]')) return  // an open dropdown owns the keys
      if (e.key === ' ') {
        if (!state.prev) return
        e.preventDefault()
        dispatch({ type: 'TOGGLE_PREV' })
        return
      }
      const times = initTimesForGroup(state.runs, state.model)  // newest first
      const i = times.indexOf(state.initTime)
      if (i === -1) return
      const ni = e.key === 'ArrowRight' ? i - 1 : i + 1  // right = newer, left = older
      if (ni < 0 || ni >= times.length) return
      e.preventDefault()
      // First step pins the current run as a baseline to scan against; further
      // steps just move, keeping that baseline. Space then swaps the two.
      if (!state.prev) dispatch({ type: 'PIN' })
      dispatch({ type: 'SELECT_INIT_TIME', initTime: times[ni] })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [state.runs, state.model, state.initTime, state.prev])

  // Derived values
  const currentKey = state.model && state.initTime ? cacheKey(state.model, state.initTime) : ''
  const prevKey = state.prev ? cacheKey(state.prev.model, state.prev.initTime) : null
  const currentEntry = cache.current.get(currentKey) ?? null
  const prevEntry = prevKey ? cache.current.get(prevKey) ?? null : null

  const initTimes = state.model ? initTimesForGroup(state.runs, state.model) : []
  const paramConfig = resolveConfig(PARAMS.find(p => p.key === state.param)!, state.variant, state.precipMode)
  // null shading follows the per-view default; an explicit pick overrides it.
  const shading = state.shading ?? defaultShading(state.model, state.param)
  const showToggle = state.prev &&
    (state.prev.model !== state.model || state.prev.initTime !== state.initTime)

  if (!state.runs.length) {
    return <div className="loading">Loading...</div>
  }

  const primaryKey = paramsForKey(state.param, state.variant, state.precipMode)[0]
  const primaryData = currentEntry?.data[primaryKey] ?? null
  const allData = currentEntry?.data ?? null
  const compareData = prevEntry?.data[primaryKey] ?? null
  const compareAllData = prevEntry?.data ?? null

  return (
    <div className="page">
      {state.error && <div className="error">{state.error}</div>}

      <nav className="chart-tabs">
        {PARAMS.map(p => (
          <button
            key={p.key}
            className={'tab' + (state.param === p.key ? ' active' : '')}
            onClick={() => dispatch({ type: 'SELECT_PARAM', param: p.key })}
          >
            {p.label}
          </button>
        ))}
      </nav>

      <header className="chart-header">
        {paramConfig.variants ? (
          <select
            className="label-select"
            value={state.variant[paramConfig.variants.key] ?? paramConfig.variants.options[0].value}
            onChange={e => dispatch({ type: 'SELECT_VARIANT', key: paramConfig.variants!.key, value: e.target.value })}
          >
            {paramConfig.variants.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : hasWindData(paramConfig) ? (
          <select
            className="label-select"
            value={state.windUnit}
            onChange={e => dispatch({ type: 'SELECT_WIND_UNIT', windUnit: e.target.value as WindUnit })}
          >
            {WIND_UNITS.map(u => <option key={u.key} value={u.key}>{u.label}</option>)}
          </select>
        ) : paramConfig.centerpiece.kind === 'precip' ? (
          <select
            className="label-select"
            value={state.precipMode}
            onChange={e => dispatch({ type: 'SELECT_PRECIP_MODE', precipMode: e.target.value as PrecipMode })}
          >
            <option value="rate">mm/h</option>
            <option value="accumulated">mm (accumulated)</option>
          </select>
        ) : <span />}

        <div className="header-title">
          <Dropdown
            value={state.model}
            options={MODELS.map(g => ({ value: g.label, label: g.label, color: MODEL_COLORS[g.label] }))}
            onSelect={(model, ctrl) => {
              if (ctrl) dispatch({ type: 'PIN' })
              dispatch({ type: 'SELECT_MODEL', model })
            }}
          />
          <>
            <span className="sep">·</span>
            <LocationPicker
              lat={state.lat} lon={state.lon}
              onSelect={(lat, lon) => dispatch({ type: 'SELECT_LOCATION', lat, lon })}
            />
          </>
        </div>
        <span />
      </header>

      <main className="chart-area">
        {primaryData && allData ? (
          <Chart
            data={primaryData}
            allData={allData}
            config={paramConfig}
            shading={shading}
            windUnit={state.windUnit}
            precipMode={state.precipMode}
            compareData={compareData}
            compareAllData={compareAllData}
          />
        ) : (
          <div className="loading">Loading {paramConfig.label}...</div>
        )}
      </main>

      <footer className="chart-footer">
        {(paramConfig.centerpiece.kind === 'filled-plume' || paramConfig.centerpiece.kind === 'precip' ||
          (paramConfig.centerpiece.kind === 'plume' && shading !== 'off')) ? (
          <DensityScale />
        ) : <span />}
        <div className="footer-center">
          <Dropdown
            value={state.initTime}
            options={initTimes.map(t => ({ value: t, label: formatTime(t) }))}
            onSelect={(initTime, ctrl) => {
              if (ctrl) dispatch({ type: 'PIN' })
              dispatch({ type: 'SELECT_INIT_TIME', initTime })
            }}
            dropUp
          />
          <button className="label-btn" onClick={refreshRuns} title="Refresh run list">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
          </button>
          <span className="sep">·</span>
          <button
            className="label-btn"
            onClick={() => dispatch({ type: 'TOGGLE_PREV' })}
            disabled={!showToggle}
            title="Swap with pinned run"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>swap_horiz</span>
            {showToggle ? ` ${state.prev!.model} ${formatTime(state.prev!.initTime)}` : ''}
          </button>
          {state.prev ? (
            <button className="label-btn" onClick={() => dispatch({ type: 'UNPIN' })} title="Unpin compare">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
            </button>
          ) : (
            <button className="label-btn" onClick={() => dispatch({ type: 'PIN' })} title="Pin current run for compare">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>push_pin</span>
            </button>
          )}
        </div>
        <select
          className="label-select"
          value={shading}
          disabled={paramConfig.centerpiece.kind === 'precip' || paramConfig.centerpiece.kind === 'filled-plume'}
          onChange={e => dispatch({ type: 'SELECT_SHADING', shading: e.target.value as Shading })}
        >
          <option value="off">Shading off</option>
          <option value="kde">Gaussian KDE</option>
          <option value="contour">Contour (bins)</option>
          <option value="adaptive">Contour (adaptive KDE)</option>
          <option value="daily">Daily min/max</option>
        </select>
      </footer>
    </div>
  )
}

createRoot(document.getElementById('root')!).render(<App />)
